// OptionsPanel.jsx — milsymbol render settings panel.
// All fields map 1-to-1 to RendererSettings setters in mil-sym-ts-web.

import React from 'react';
import {
    Box, Stack, Typography, Select, MenuItem, Slider, Divider,
    FormControlLabel, Switch, Tooltip,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {useMilsymbolSettings} from '../map/editor/milsymbolSettings';

const SectionHeader = ({children}) => (
    <Typography
        variant="caption"
        sx={{
            display: 'block',
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 0.6,
            textTransform: 'uppercase',
            color: 'text.disabled',
            mt: 1.5,
            mb: 0.5,
        }}
    >
        {children}
    </Typography>
);

const FieldRow = ({label, hint, children}) => (
    <Stack direction="row" alignItems="center" gap={1} sx={{mb: 0.75}}>
        <Stack direction="row" alignItems="center" gap={0.25} sx={{width: 110, flexShrink: 0}}>
            <Typography variant="caption" sx={{fontSize: 11, color: 'text.secondary'}}>
                {label}
            </Typography>
            {hint && (
                <Tooltip title={hint} placement="top">
                    <InfoOutlinedIcon sx={{fontSize: 12, color: 'text.disabled'}}/>
                </Tooltip>
            )}
        </Stack>
        <Box sx={{flex: 1, minWidth: 0}}>
            {children}
        </Box>
    </Stack>
);

const smallSelectSx = {
    fontSize: 12,
    '& .MuiSelect-select': {py: '4px', fontSize: 12},
};

const OptionsPanel = () => {
    const {settings, updateSettings} = useMilsymbolSettings();

    return (
        <Stack sx={{height: '100%', overflow: 'hidden'}}>
            {/* Header */}
            <Box
                sx={(theme) => ({
                    px: 1.5, py: 0.75,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    flexShrink: 0,
                    bgcolor: 'background.default',
                })}
            >
                <Typography variant="caption" sx={{fontWeight: 700, fontSize: 11, letterSpacing: 0.3, textTransform: 'uppercase', color: 'text.disabled'}}>
                    Symbol Options
                </Typography>
            </Box>

            <Box sx={{flex: 1, overflowY: 'auto', px: 1.5, py: 1}}>
                <SectionHeader>Rendering</SectionHeader>

                {/* RendererSettings.setSinglePointSymbolOutlineWidth */}
                <FieldRow label="Outline width" hint="Single-point symbol outline width in pixels">
                    <Stack direction="row" alignItems="center" gap={1}>
                        <Slider
                            size="small"
                            min={0}
                            max={6}
                            step={1}
                            value={settings.outlineWidth}
                            onChange={(_, v) => updateSettings({outlineWidth: v})}
                            sx={{flex: 1}}
                        />
                        <Typography variant="caption" sx={{width: 28, textAlign: 'right', fontSize: 11, color: 'text.secondary'}}>
                            {settings.outlineWidth}
                        </Typography>
                    </Stack>
                </FieldRow>

                <Divider sx={{my: 1}}/>
                <SectionHeader>Labels</SectionHeader>

                {/* RendererSettings.setTextBackgroundMethod */}
                <FieldRow label="Text background" hint="Background style for modifier text labels">
                    <Select
                        size="small"
                        fullWidth
                        value={settings.textBackgroundMethod}
                        onChange={(e) => updateSettings({textBackgroundMethod: e.target.value})}
                        sx={smallSelectSx}
                    >
                        <MenuItem value={0} sx={{fontSize: 12}}>None</MenuItem>
                        <MenuItem value={1} sx={{fontSize: 12}}>Color fill</MenuItem>
                        <MenuItem value={2} sx={{fontSize: 12}}>Outline</MenuItem>
                    </Select>
                </FieldRow>

                {/* RendererSettings.setSPModifierPlacement */}
                <FieldRow label="Label placement" hint="Strict: labels stay in assigned positions. Flexible: labels collapse vertically to fill gaps.">
                    <Select
                        size="small"
                        fullWidth
                        value={settings.spModifierPlacement}
                        onChange={(e) => updateSettings({spModifierPlacement: e.target.value})}
                        sx={smallSelectSx}
                    >
                        <MenuItem value={0} sx={{fontSize: 12}}>Strict</MenuItem>
                        <MenuItem value={1} sx={{fontSize: 12}}>Flexible</MenuItem>
                    </Select>
                </FieldRow>

                <Divider sx={{my: 1}}/>
                <SectionHeader>Display</SectionHeader>

                {/* RendererSettings.setOperationalConditionModifierType */}
                <FieldRow label="OC modifier" hint="How the operational condition modifier is rendered (slash or bar)">
                    <Select
                        size="small"
                        fullWidth
                        value={settings.ocmType}
                        onChange={(e) => updateSettings({ocmType: e.target.value})}
                        sx={smallSelectSx}
                    >
                        <MenuItem value={0} sx={{fontSize: 12}}>Slash ( / )</MenuItem>
                        <MenuItem value={1} sx={{fontSize: 12}}>Bar ( — )</MenuItem>
                    </Select>
                </FieldRow>

                {/* RendererSettings.setAutoCollapseModifiers */}
                <FormControlLabel
                    control={
                        <Switch
                            size="small"
                            checked={settings.autoCollapseModifiers}
                            onChange={(e) => updateSettings({autoCollapseModifiers: e.target.checked})}
                        />
                    }
                    label={<Typography variant="caption" sx={{fontSize: 11}}>Auto-collapse modifiers (fire support areas)</Typography>}
                    sx={{ml: 0, gap: 1}}
                />

                {/* RendererSettings.setCenterOnHQStaff */}
                <FormControlLabel
                    control={
                        <Switch
                            size="small"
                            checked={settings.centerOnHQStaff}
                            onChange={(e) => updateSettings({centerOnHQStaff: e.target.checked})}
                        />
                    }
                    label={<Typography variant="caption" sx={{fontSize: 11}}>Center HQ on staff end</Typography>}
                    sx={{ml: 0, gap: 1, mt: 0.5}}
                />
            </Box>
        </Stack>
    );
};

export default OptionsPanel;
